import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Footer } from "../../layouts/footer";
import config from "../../../config";
import axios from "axios";

const ViewProduct = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${config.baseURL}/productsfrontend/${id}`);
        // Ensure all fields are initialized, handling potential nulls/undefined from API
        const fetchedData = response.data;
        setFormData({
          name: fetchedData.name || "",
          bore: fetchedData.bore || "",
          thick: fetchedData.thick || "",
          thickness_tolerance: fetchedData.thickness_tolerance || "",
          free_diameter: fetchedData.free_diameter || "",
          clearance_onbore: fetchedData.clearance_onbore || "",
          beam: fetchedData.beam || "",
          lug: fetchedData.lug || "",
          hole_diamter: fetchedData.hole_diamter || "",
          ring_load_carrying_capacity: fetchedData.ring_load_carrying_capacity || "",
          groove_diameter: fetchedData.groove_diameter || "",
          groove_tolerance: fetchedData.groove_tolerance || "",
          groove_width: fetchedData.groove_width || "",
          edge_margin: fetchedData.edge_margin || "",
          groove_load_capacity: fetchedData.groove_load_capacity || "",
          catname: fetchedData.catname || "",
          price_1_249: fetchedData.price_1_249 ? String(fetchedData.price_1_249) : "",
          price_250_999: fetchedData.price_250_999 ? String(fetchedData.price_250_999) : "",
          price_1000_4999: fetchedData.price_1000_4999 ? String(fetchedData.price_1000_4999) : "",
          price_5000_9999: fetchedData.price_5000_9999 ? String(fetchedData.price_5000_9999) : "",
          price_10000plus: fetchedData.price_10000plus ? String(fetchedData.price_10000plus) : "",
        });
      } catch (err) {
        console.error("Error fetching product data:", err);
        setError("Failed to load product data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Define input fields dynamically to reduce repetition, all are readOnly for ViewProduct
  const productInfoFields = [
    { id: "catname", name: "catname", label: "Product Category", placeholder: "" },
    { id: "name", name: "name", label: "Product Name", placeholder: "" },
    { id: "bore", name: "bore", label: "Bore", placeholder: "" },
    { id: "thick", name: "thick", label: "Thick", placeholder: "" },
    { id: "thickness_tolerance", name: "thickness_tolerance", label: "Thickness Tolerance", placeholder: "" },
    { id: "free_diameter", name: "free_diameter", label: "Free Diameter", placeholder: "" },
    { id: "clearance_onbore", name: "clearance_onbore", label: "Clearance On Bore", placeholder: "" },
    { id: "beam", name: "beam", label: "Beam", placeholder: "" },
    { id: "lug", name: "lug", label: "Lug", placeholder: "" },
    { id: "hole_diamter", name: "hole_diamter", label: "Hole Diameter", placeholder: "" },
    { id: "ring_load_carrying_capacity", name: "ring_load_carrying_capacity", label: "Ring Load Carrying Capacity", placeholder: "" },
    { id: "groove_diameter", name: "groove_diameter", label: "Groove Diameter", placeholder: "" },
    { id: "groove_tolerance", name: "groove_tolerance", label: "Groove Tolerance", placeholder: "" },
    { id: "groove_width", name: "groove_width", label: "Groove Width", placeholder: "" },
    { id: "edge_margin", name: "edge_margin", label: "Edge Margin", placeholder: "" },
    { id: "groove_load_capacity", name: "groove_load_capacity", label: "Groove Load Capacity", placeholder: "" },
  ];

  const pricingFields = [
    { id: "price_1_249", name: "price_1_249", label: "Price (1 to 249 units)", placeholder: "" },
    { id: "price_250_999", name: "price_250_999", label: "Price (250 to 999 units)", placeholder: "" },
    { id: "price_1000_4999", name: "price_1000_4999", label: "Price (1000 to 4999 units)", placeholder: "" },
    { id: "price_5000_9999", name: "price_5000_9999", label: "Price (5000 to 9999 units)", placeholder: "" },
    { id: "price_10000plus", name: "price_10000plus", label: "Price (10000+ units)", placeholder: "" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <p className="text-xl">Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <p className="text-xl text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-4xl bg-gray-800 rounded-lg shadow-xl overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-700">
          <p className="text-2xl font-bold text-white">Product Details</p>
          <p className="mt-1 text-gray-400">Here are the detailed specifications and pricing for this product.</p>
        </div>
        <div className="p-6">
          <div className="space-y-8">
            {/* Product Information Section */}
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                General Information
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Key specifications of the product.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {productInfoFields.map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label}
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        value={formData[field.name] || "N/A"} // Display "N/A" if value is empty
                        className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 cursor-not-allowed sm:text-sm sm:leading-6"
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Information Section */}
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                Pricing Details
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Breakdown of prices per unit based on quantity.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {pricingFields.map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label} ($)
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        value={formData[field.name] || "N/A"} // Display "N/A" if value is empty
                        className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 cursor-not-allowed sm:text-sm sm:leading-6"
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ViewProduct;
